/**
 * Auto-balancing for the per-call rubric weight editor.
 *
 * The ten dimension weights always sum to WEIGHT_TOTAL. Raising one therefore
 * has to take the difference from the others, and the order matters: the two
 * Up-Front Contract dimensions and both Pain dimensions are the ones a sales
 * manager least wants quietly drained, so everything else gives first.
 *
 * Kept free of React so the invariants can be tested directly — see
 * rubricWeightBalancer.test.ts.
 */

import {
  RUBRIC_DIMENSION_KEYS,
  PROTECTED_DIMENSION_KEYS,
  MIN_WEIGHT,
  MAX_WEIGHT,
  WEIGHT_STEP,
  WEIGHT_TOTAL,
  WEIGHT_SUM_EPSILON
} from '../constants/rubricDimensions';

export type WeightMap = Record<string, number>;

/** Weights are stored on a 0.1 grid; compare and round in whole steps to keep
 *  float error out of the sum invariant entirely. */
const toSteps = (weight: number) => Math.round(weight / WEIGHT_STEP);
const fromSteps = (steps: number) => Number((steps * WEIGHT_STEP).toFixed(1));

const MIN_STEPS = toSteps(MIN_WEIGHT);
const MAX_STEPS = toSteps(MAX_WEIGHT);

/**
 * Distribute `needed` steps across donors, proportional to each donor's
 * headroom in the direction of travel.
 *
 * Headroom-proportional rather than value-proportional: a donor already at its
 * floor has zero headroom and absorbs nothing, so a single pass can never push
 * anyone past a bound. Largest-remainder assignment keeps the integer split
 * exact.
 *
 * @returns Steps actually absorbed (< needed when the donors ran out of room).
 */
const distribute = (
  steps: Record<string, number>,
  donors: string[],
  needed: number
): number => {
  if (needed === 0 || donors.length === 0) return 0;

  const draining = needed > 0;
  const magnitude = Math.abs(needed);
  const headroom = new Map<string, number>();
  let totalHeadroom = 0;

  for (const key of donors) {
    const room = draining ? steps[key] - MIN_STEPS : MAX_STEPS - steps[key];
    if (room > 0) {
      headroom.set(key, room);
      totalHeadroom += room;
    }
  }
  if (totalHeadroom === 0) return 0;

  const absorbable = Math.min(magnitude, totalHeadroom);

  // Exact shares first, then hand out the leftover by largest fractional part.
  const exact = [...headroom.entries()].map(([key, room]) => {
    const share = (room / totalHeadroom) * absorbable;
    return { key, room, whole: Math.floor(share), fraction: share - Math.floor(share) };
  });

  let assigned = exact.reduce((sum, entry) => sum + entry.whole, 0);
  const byFraction = [...exact].sort((a, b) => b.fraction - a.fraction || b.room - a.room);
  let index = 0;
  while (assigned < absorbable && index < byFraction.length * 2) {
    const candidate = byFraction[index % byFraction.length];
    if (candidate.whole < candidate.room) {
      candidate.whole += 1;
      assigned += 1;
    }
    index += 1;
  }

  for (const { key, whole } of exact) {
    steps[key] += draining ? -whole : whole;
  }
  return draining ? assigned : -assigned;
};

/**
 * Set one dimension's weight, rebalancing the rest to preserve the total.
 *
 * Unprotected dimensions absorb the change first; protected ones only give once
 * the unprotected pool is exhausted. Locked dimensions never move. If the whole
 * donor pool runs out of room, `targetWeight` is clamped to what could actually
 * be absorbed — the caller sees the returned weight stop moving.
 *
 * @param current Current weights (not mutated).
 * @param key Dimension being changed.
 * @param targetWeight Requested new weight.
 * @param lockedKeys Dimensions the admin pinned.
 * @returns A new weight map that still sums to WEIGHT_TOTAL.
 */
export const applyWeightChange = (
  current: WeightMap,
  key: string,
  targetWeight: number,
  lockedKeys: string[] = []
): WeightMap => {
  if (!RUBRIC_DIMENSION_KEYS.includes(key)) return { ...current };

  const steps: Record<string, number> = {};
  for (const dimension of RUBRIC_DIMENSION_KEYS) {
    steps[dimension] = toSteps(current[dimension] ?? WEIGHT_TOTAL / RUBRIC_DIMENSION_KEYS.length);
  }

  const locked = new Set(lockedKeys);
  const requested = Math.max(MIN_STEPS, Math.min(MAX_STEPS, toSteps(targetWeight)));
  const delta = requested - steps[key];
  if (delta === 0) return { ...current };

  const donors = RUBRIC_DIMENSION_KEYS.filter((d) => d !== key && !locked.has(d));
  const unprotected = donors.filter((d) => !PROTECTED_DIMENSION_KEYS.includes(d));
  const protectedDonors = donors.filter((d) => PROTECTED_DIMENSION_KEYS.includes(d));

  // Unprotected pool first, protected only for the remainder.
  const absorbedByUnprotected = distribute(steps, unprotected, delta);
  const remaining = delta - absorbedByUnprotected;
  const absorbedByProtected = distribute(steps, protectedDonors, remaining);

  // Clamp to what the donor pool could actually take.
  steps[key] += absorbedByUnprotected + absorbedByProtected;

  const result: WeightMap = {};
  for (const dimension of RUBRIC_DIMENSION_KEYS) {
    result[dimension] = fromSteps(steps[dimension]);
  }
  return result;
};

/** The equal-weight baseline every session is graded at until it is retuned. */
export const equalWeights = (): WeightMap =>
  RUBRIC_DIMENSION_KEYS.reduce<WeightMap>(
    (acc, key) => ({ ...acc, [key]: WEIGHT_TOTAL / RUBRIC_DIMENSION_KEYS.length }),
    {}
  );

/** Sum of all weights — should always equal WEIGHT_TOTAL. */
export const sumWeights = (weights: WeightMap): number =>
  Number(
    RUBRIC_DIMENSION_KEYS.reduce((total, key) => total + (weights[key] ?? 0), 0).toFixed(2)
  );

/** True when the weights are a valid, saveable set. */
export const isBalanced = (weights: WeightMap): boolean =>
  Math.abs(sumWeights(weights) - WEIGHT_TOTAL) < WEIGHT_SUM_EPSILON;

/** True when any dimension deviates from the equal-weight baseline. */
export const isCustomWeighted = (weights: WeightMap): boolean => {
  const baseline = WEIGHT_TOTAL / RUBRIC_DIMENSION_KEYS.length;
  return RUBRIC_DIMENSION_KEYS.some(
    (key) => Math.abs((weights[key] ?? baseline) - baseline) > WEIGHT_SUM_EPSILON
  );
};

/** Dimension keys whose weight differs between two maps, in rubric order. */
export const changedKeys = (next: WeightMap, previous: WeightMap): string[] =>
  RUBRIC_DIMENSION_KEYS.filter(
    (key) => Math.abs((next[key] ?? 0) - (previous[key] ?? 0)) > WEIGHT_SUM_EPSILON
  );

/** A dimension's share of the composite score, as a percentage (0-100). */
export const weightShare = (key: string, weights: WeightMap): number => {
  const total = sumWeights(weights);
  return total > 0 ? ((weights[key] ?? 0) / total) * 100 : 0;
};

/**
 * Recompute the weighted total a set of scores would produce.
 *
 * Mirrors the analyzer's arithmetic (`Σ raw · weight` over graded categories
 * only) so the drawer can preview the new score without a round trip.
 */
export const previewWeightedTotal = (
  scoresByKey: Record<string, number | null | undefined>,
  weights: WeightMap
): { total: number; max: number; percentage: number } => {
  let total = 0;
  let weightSum = 0;
  for (const key of RUBRIC_DIMENSION_KEYS) {
    const score = scoresByKey[key];
    if (score == null) continue; // ungraded dimensions are excluded from both sides
    const weight = weights[key] ?? 0;
    total += score * weight;
    weightSum += weight;
  }
  const max = weightSum * 5;
  return {
    total: Number(total.toFixed(2)),
    max: Number(max.toFixed(2)),
    percentage: max > 0 ? total / max : 0
  };
};
